import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useApi } from '../hooks/useApi';
import { useUser } from '../hooks/useUser';
import { sharedStyles } from '../styles/sharedStyles';

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'operator', label: 'Operator' },
  { value: 'foreman', label: 'Foreman' }
];

const Users = () => {
  const api = useApi();
  const { user: currentUser, loading: userLoading } = useUser();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [role, setRole] = useState('operator');
  const [customerSearch, setCustomerSearch] = useState('');
  const [allCustomersList, setAllCustomersList] = useState([]);
  const [allCustomersLoading, setAllCustomersLoading] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api({ method: 'get', url: '/users' });
      if (res.data?.success) {
        setUsers(res.data.data || []);
      } else {
        setError(res.data?.message || 'Failed to load users');
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      loadUsers();
    }
  }, [currentUser?.role, loadUsers]);

  useEffect(() => {
    if (!editUser || role !== 'foreman') {
      setAllCustomersList([]);
      return;
    }
    let cancelled = false;
    setAllCustomersLoading(true);
    (async () => {
      try {
        const res = await api({ method: 'get', url: '/customers/dropdown' });
        if (!cancelled && res.data?.success) {
          const list = res.data.data;
          setAllCustomersList(Array.isArray(list) ? list : []);
        }
      } catch {
        if (!cancelled) setAllCustomersList([]);
      } finally {
        if (!cancelled) setAllCustomersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editUser, role, api]);

  const customersAvailableToAdd = useMemo(() => {
    const selectedIds = new Set(selectedCustomers.map((c) => c.id));
    let list = allCustomersList.filter((c) => c.id != null && !selectedIds.has(c.id));
    const q = customerSearch.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (c) =>
          String(c.companyName || '')
            .toLowerCase()
            .includes(q) ||
          String(c.contactPerson || '')
            .toLowerCase()
            .includes(q) ||
          String(c.email || '')
            .toLowerCase()
            .includes(q)
      );
    }
    return [...list].sort((a, b) =>
      String(a.companyName || '').localeCompare(String(b.companyName || ''), undefined, {
        sensitivity: 'base'
      })
    );
  }, [allCustomersList, selectedCustomers, customerSearch]);

  const openEdit = (u) => {
    setEditUser(u);
    setRole(u.role || 'operator');
    setSelectedCustomers(u.assignedCustomers ? [...u.assignedCustomers] : []);
    setCustomerSearch('');
  };

  const closeEdit = () => {
    setEditUser(null);
    setSaving(false);
    setAllCustomersList([]);
    setCustomerSearch('');
  };

  const addCustomer = (c) => {
    if (selectedCustomers.some((x) => x.id === c.id)) return;
    setSelectedCustomers((prev) => [...prev, { id: c.id, companyName: c.companyName, contactPerson: c.contactPerson }]);
  };

  const removeCustomer = (id) => {
    setSelectedCustomers((prev) => prev.filter((c) => c.id !== id));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editUser) return;
    if (currentUser?.id === editUser.id && role !== 'admin') {
      setError('You cannot change your own role from admin. Ask another admin to update your account.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const body = { role };
      if (role === 'foreman') {
        body.foremanCustomerIds = selectedCustomers.map((c) => c.id);
      }
      const res = await api({
        method: 'put',
        url: `/users/${editUser.id}`,
        data: body
      });
      if (res.data?.success) {
        await loadUsers();
        closeEdit();
      } else {
        setError(res.data?.message || 'Update failed');
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = users.filter((u) => {
    const term = searchTerm.trim().toLowerCase();
    const matchesSearch =
      !term || (u.email || '').toLowerCase().includes(term);
    const matchesRole = !roleFilter || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  if (userLoading) {
    return (
      <div style={sharedStyles.pageContainer} className="container-fluid px-2 px-md-4 py-4">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  if (currentUser?.role !== 'admin') {
    return (
      <div style={sharedStyles.pageContainer} className="container-fluid px-2 px-md-4 py-4">
        <div className="alert alert-warning">You do not have access to this page.</div>
      </div>
    );
  }

  return (
    <div style={sharedStyles.pageContainer} className="container-fluid px-2 px-md-4 py-4">
      <h2 style={sharedStyles.pageTitle} className="mb-4">Users</h2>
      {error && !editUser && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')} aria-label="Close" />
        </div>
      )}

      {loading ? (
        <div className="spinner-border text-primary" role="status" />
      ) : (
        <>
          <div className="card mb-3" style={sharedStyles.card}>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-12 col-md-8">
                  <label className="form-label">Search</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by email"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label">Role</label>
                  <select
                    className="form-select"
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                  >
                    <option value="">All roles</option>
                    {ROLES.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Foreman customers</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.id}>
                  <td>{u.email}</td>
                  <td>{u.role}</td>
                  <td style={{ maxWidth: 280 }}>
                    {u.role === 'foreman' && u.assignedCustomers?.length
                      ? u.assignedCustomers.map((c) => c.companyName).join(', ')
                      : '—'}
                  </td>
                  <td>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openEdit(u)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan="4" className="text-center text-muted py-4">
                    No users match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </>
      )}

      {editUser && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1400 }}
            onClick={closeEdit}
            aria-hidden="true"
          />
          <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1500 }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit user: {editUser.email}</h5>
                <button type="button" className="btn-close" onClick={closeEdit} aria-label="Close" />
              </div>
              <form onSubmit={handleSave}>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger py-2">{error}</div>
                  )}
                  <div className="mb-3">
                    <label className="form-label">Role</label>
                    <select className="form-select" value={role} onChange={(e) => setRole(e.target.value)}>
                      {ROLES.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>

                  {role === 'foreman' && (
                    <div className="mb-2">
                      <label className="form-label">Assigned customers</label>
                      <p className="text-muted small mb-2">
                        All customers load below. Use the search box to filter the list, or scroll to pick any
                        customer. Already-assigned customers are hidden from the list.
                      </p>
                      <input
                        type="search"
                        className="form-control mb-2"
                        placeholder="Filter by company, contact, or email…"
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        autoComplete="off"
                        disabled={allCustomersLoading}
                      />
                      {allCustomersLoading ? (
                        <p className="text-muted small mb-2">Loading customers…</p>
                      ) : (
                        <p className="text-muted small mb-2">
                          Showing {customersAvailableToAdd.length} of{' '}
                          {Math.max(0, allCustomersList.length - selectedCustomers.length)} not yet assigned
                          {allCustomersList.length > 0 ? ` (${allCustomersList.length} total in directory)` : ''}.
                        </p>
                      )}
                      <div
                        className="border rounded mb-3 bg-white"
                        style={{ maxHeight: 'min(280px, 40vh)', overflowY: 'auto' }}
                      >
                        {allCustomersLoading ? (
                          <div className="p-3 text-muted small text-center">Loading…</div>
                        ) : customersAvailableToAdd.length === 0 ? (
                          <div className="p-3 text-muted small text-center">
                            {allCustomersList.length === 0
                              ? 'No customers found.'
                              : customerSearch.trim()
                                ? 'No matches — clear the filter to see all.'
                                : 'Every customer is already assigned above, or the list is empty.'}
                          </div>
                        ) : (
                          <ul className="list-group list-group-flush">
                            {customersAvailableToAdd.map((c) => (
                              <li
                                key={c.id}
                                className="list-group-item d-flex justify-content-between align-items-center py-2 px-3"
                              >
                                <div className="me-2 min-w-0">
                                  <div className="fw-medium text-truncate">{c.companyName}</div>
                                  {(c.contactPerson || c.email) && (
                                    <div className="small text-muted text-truncate">
                                      {[c.contactPerson, c.email].filter(Boolean).join(' · ')}
                                    </div>
                                  )}
                                </div>
                                <button type="button" className="btn btn-sm btn-primary flex-shrink-0" onClick={() => addCustomer(c)}>
                                  Add
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="small text-muted mb-1">Currently assigned</div>
                      <div>
                        {selectedCustomers.length === 0 ? (
                          <span className="text-muted small">None — add from the list above.</span>
                        ) : (
                          selectedCustomers.map((c) => (
                            <span key={c.id} className="badge bg-secondary me-1 mb-1">
                              {c.companyName}
                              <button
                                type="button"
                                className="btn btn-link btn-sm text-white text-decoration-none p-0 ms-1"
                                onClick={() => removeCustomer(c.id)}
                                aria-label="Remove"
                              >
                                ×
                              </button>
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeEdit} disabled={saving}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Users;
